import React from 'react';
import { Box, Typography, Container, Paper, Divider, List, ListItem } from '@mui/material';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';

const ApiDocumentation = () => {
  const apiEndpoints = [
    {
      name: 'Liste des journaux',
      description: 'Récupérer la liste des journaux disponibles pour la publication',
      endpoint: '/annonces-legales/journaux',
      method: 'GET',
      authorization: 'Bearer Token',
      responseExample: {
        success: true,
        data: {
          journaux: [
            {
              id: 1,
              name: "Journal Officiel",
              region: "Île-de-France"
            }
          ]
        }
      }
    },
    {
      name: 'Types d\'annonces',
      description: 'Récupérer la liste des types d\'annonces légales disponibles',
      endpoint: '/annonces-legales/types-annonces',
      method: 'GET',
      authorization: 'Bearer Token',
      responseExample: {
        success: true,
        data: {
          types: [
            {
              id: "sarl",
              name: "Création de SARL",
              category: "Création d'entreprise"
            }
          ]
        }
      }
    },
    {
      name: 'Création de publication',
      description: 'Créer une nouvelle publication d\'annonce légale',
      endpoint: '/annonces-legales/publication',
      method: 'POST',
      authorization: 'Bearer Token',
      requestExample: {
        journal_id: 1,
        type_annonce: "sarl",
        content: {
          company_name: "Ma Société SARL",
          capital: 10000,
          address: "1 rue de la Paix, 75001 Paris"
        }
      },
      responseExample: {
        success: true,
        data: {
          publication_id: "123456",
          status: "pending",
          created_at: "2023-05-30T10:00:00Z"
        }
      }
    },
    {
      name: 'Liste des annonces',
      description: 'Récupérer la liste des annonces légales',
      endpoint: '/annonces-legales/list',
      method: 'GET',
      authorization: 'Bearer Token',
      parameters: [
        { name: 'page', type: 'number', description: 'Numéro de la page', required: false }
      ],
      responseExample: {
        success: true,
        data: {
          announcements: [
            {
              id: "123456",
              type: "sarl",
              status: "published",
              journal: "Journal Officiel",
              publication_date: "2023-05-30"
            }
          ]
        }
      }
    },
    {
      name: 'Attestation',
      description: 'Récupérer l\'attestation d\'une annonce légale',
      endpoint: '/annonces-legales/attestation',
      method: 'GET',
      authorization: 'Bearer Token',
      parameters: [
        { name: 'announcement_id', type: 'string', description: 'ID de l\'annonce', required: true }
      ],
      responseExample: {
        success: true,
        data: {
          attestation_url: "https://api.busyplace.fr/documents/attestation-123456.pdf",
          expires_at: "2023-06-30T10:00:00Z"
        }
      }
    },
    {
      name: 'Justificatif',
      description: 'Récupérer le justificatif de publication d\'une annonce légale',
      endpoint: '/annonces-legales/justificatif',
      method: 'GET',
      authorization: 'Bearer Token',
      parameters: [
        { name: 'announcement_id', type: 'string', description: 'ID de l\'annonce', required: true }
      ],
      responseExample: {
        success: true,
        data: {
          justificatif_url: "https://api.busyplace.fr/documents/justificatif-123456.pdf",
          publication_date: "2023-05-30"
        }
      }
    }
  ];

  const CodeBlock = ({ code }) => (
    <SyntaxHighlighter 
      language="json"
      style={atomOneDark}
      customStyle={{
        borderRadius: '4px',
        padding: '16px',
        marginTop: '8px',
        marginBottom: '16px'
      }}
    >
      {JSON.stringify(code, null, 2)}
    </SyntaxHighlighter>
  );

  const EndpointSection = ({ endpoint }) => (
    <Box sx={{ mb: 6 }}>
      <Paper sx={{ p: 3, bgcolor: '#fff', mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 2, color: '#1976d2', fontWeight: 'bold' }}>
          {endpoint.name}
        </Typography>
        <Typography sx={{ mb: 3 }}>
          {endpoint.description}
        </Typography>
        
        <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>
          Endpoint
        </Typography>
        <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 3, display: 'flex', alignItems: 'center' }}>
          <Typography 
            sx={{ 
              color: endpoint.method === 'GET' ? '#4CAF50' : '#2196F3',
              fontWeight: 'bold',
              mr: 2
            }}
          >
            {endpoint.method}
          </Typography>
          <Typography component="code">
            {endpoint.endpoint}
          </Typography>
        </Paper>

        <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>
          Authorization
        </Typography>
        <Typography sx={{ mb: 3 }}>
          Vous devez inclure votre {endpoint.authorization} dans l'en-tête de la requête:
        </Typography>
        <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 3 }}>
          <Typography component="code">
            Authorization: Bearer YOUR_API_TOKEN
          </Typography>
        </Paper>

        {endpoint.parameters && endpoint.parameters.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>
              Parameters
            </Typography>
            <List sx={{ mb: 3 }}>
              {endpoint.parameters.map((param, index) => (
                <ListItem key={index} sx={{ display: 'block', py: 1 }}>
                  <Typography sx={{ fontWeight: 'bold' }}>
                    {param.name} 
                    {param.required && <span style={{ color: 'red' }}> *</span>}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Type: {param.type}
                  </Typography>
                  <Typography variant="body2">
                    {param.description}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {endpoint.requestExample && (
          <>
            <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>
              Example Request
            </Typography>
            <CodeBlock code={endpoint.requestExample} />
          </>
        )}

        <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>
          Example Response
        </Typography>
        <CodeBlock code={endpoint.responseExample} />
      </Paper>
      
      <Divider sx={{ my: 3 }} />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h3" sx={{ mb: 3, color: '#1976d2' }}>
          API Documentation (1.0.0)
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Support technique:
          </Typography>
          <Typography component="a" href="mailto:support@busyplace.fr" sx={{ color: '#1976d2', textDecoration: 'none' }}>
            support@busyplace.fr
          </Typography>
        </Box>

        <Typography sx={{ mb: 3 }}>
          L'API vous permet d'accéder et de gérer les annonces légales. Cette documentation fournit les informations nécessaires pour intégrer notre API dans votre application.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="text.secondary">
            URL de base:
          </Typography>
          <Paper sx={{ p: 2, bgcolor: '#fff' }}>
            <Typography component="code" sx={{ color: '#1976d2' }}>
            https://api.busyplace.fr/v1
            </Typography>
          </Paper>
        </Box>
      </Paper>

      {apiEndpoints.map((endpoint, index) => (
        <EndpointSection key={index} endpoint={endpoint} />
      ))}
    </Container>
  );
};

export default ApiDocumentation;
